import dragula from 'dragula';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { resetChildrenPosition } from '../../helpers/reset-children-position';
import type { Layout, LayoutWidget } from '../../redux/config/layout';
import { useUpdateLayoutWidgetMutation } from '../../redux/config/layout-widget';
import { selectActiveTabId } from '../../redux/slices/active-layout-tab-id-slice';
import { getWidgets } from '../../redux/slices/layout-tabs-slice';
import { selectStagedWidgetDimensions } from '../../redux/slices/update-width-height-of-chart';
import { addWidgetsReorder } from '../../redux/slices/widgetChart/WidgetChartSlice';
import type { DragulaLike } from '../../types/dragula-like';
import { useEditSaveDiscard } from '../Charts/hooks/useSaveEdit';
import { getRenderableLayoutWidgets, getWidgetLayoutIds, updateDataPosition } from './layout-tab.functions';
import { TabWidget } from './tab-widget.component';

type TabElementProps = {
  layout: Layout;
  assignChildDraggableContainer: (
    editEnabled: boolean,
    drakeRef: React.MutableRefObject<DragulaLike | undefined>,
    childContainer: HTMLElement | null
  ) => void;
  isEditable: boolean;
};

export const TabContent = ({ layout, assignChildDraggableContainer, isEditable }: TabElementProps) => {
  const dispatch = useDispatch();
  const [updateLayoutWidget] = useUpdateLayoutWidgetMutation();
  const tabId = useRef<string>(`mainWidgetCard-${layout.id}`);
  // Using the type from @types/dragula. The default export returns a DragulaDrake drake instance.
  const drakeWidgetRef = useRef<DragulaLike | undefined>(undefined);
  const [currentTabRef, setCurrentTabRef] = useState<HTMLDivElement>();
  const currentTabElementRef = useRef<HTMLDivElement | null>(null);
  const saveChangesEdit = useEditSaveDiscard();
  const widgetsReorderd = useRef(false);
  const activeTabId = useSelector(selectActiveTabId);
  const activeTabWidgets = useSelector(getWidgets);
  const stagedWidgetDimensions = useSelector(selectStagedWidgetDimensions);

  useEffect(() => {
    if (saveChangesEdit !== undefined && currentTabRef && widgetsReorderd.current) {
      if (saveChangesEdit) {
        const widgetIds = getWidgetLayoutIds(currentTabRef.children);
        updateLayoutWidget(widgetIds);
        updateDataPosition(currentTabRef.children);
      } else {
        resetChildrenPosition(currentTabRef);
      }
    }
  }, [saveChangesEdit, currentTabRef, updateLayoutWidget]);

  useEffect(() => {
    if (currentTabRef) {
      assignChildDraggableContainer(isEditable, drakeWidgetRef, currentTabRef);
    }
  }, [currentTabRef, isEditable, assignChildDraggableContainer]);

  useEffect(() => {
    drakeWidgetRef.current = dragula([], {
      direction: 'vertical',
    });

    drakeWidgetRef.current?.on('drop', (element: Element, target: Element) => {
      const widgetIds = getWidgetLayoutIds(target.children);
      widgetsReorderd.current = true;

      if (widgetIds) {
        dispatch(addWidgetsReorder(widgetIds));
      }
    });

    // Wheel events are registered in drag mode on the mirrored element.
    // Once the cloned element is appended to the DOM we listen for wheel events
    // and modify the parent scroll position. We formerly used a module augmentation
    // for the 'cloned' event; now we rely on the permissive DragulaLike.on signature.
    drakeWidgetRef.current?.on('cloned', (clone: Element) => {
      const parentElement = document.getElementById(tabId.current)?.parentElement;
      if (parentElement) {
        (clone as HTMLDivElement).addEventListener('wheel', (e) => {
          parentElement.scrollBy({
            top: e.deltaY,
          });
        });
      }
    });

    return () => {
      drakeWidgetRef.current?.destroy();
    };
  }, [dispatch]);

  const sortByPosition = useCallback((arr: LayoutWidget[]) => {
    return Array.from(arr).sort((a, b) => a.position - b.position);
  }, []);

  const renderableWidgets = useMemo(
    () =>
      getRenderableLayoutWidgets(
        layout.layoutWidgets,
        activeTabWidgets,
        layout.id,
        activeTabId,
        stagedWidgetDimensions
      ),
    [layout.layoutWidgets, activeTabWidgets, layout.id, activeTabId, stagedWidgetDimensions]
  );

  const handleCurrentTabRef = useCallback((el: HTMLDivElement | null) => {
    if (!el || currentTabElementRef.current === el) return;

    currentTabElementRef.current = el;
    setCurrentTabRef(el);
  }, []);

  return (
    <div
      className="flex h-full flex-wrap @container"
      data-tab={layout.title}
      id={tabId.current}
      key={layout.id}
      ref={handleCurrentTabRef}
    >
      {sortByPosition(renderableWidgets)?.map((layoutWidget) => (
        <TabWidget layoutWidget={layoutWidget} layout={layout} key={layoutWidget.id} />
      ))}
    </div>
  );
};
